import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Upload, Download, History, Sparkles, Trash2, CalendarCheck, Loader2, FileSpreadsheet, Zap, Eye, Heart, MessageCircle, Send, Bookmark, Wand2, GripVertical, ThumbsUp, Share2, Music2 } from "lucide-react";
import { api, authUrl, formatApiError, formatDateShort, mediaSrc } from "@/lib/api";
import { FORMATS } from "@/lib/constants";

const PP_STATUS = {
  draft: { label: "Bozza PostPickr", cls: "bg-stone-500 text-white" },
  scheduled: { label: "Programmato", cls: "bg-amber-600 text-white" },
  published: { label: "Pubblicato", cls: "bg-emerald-700 text-white" },
};

function ppDate(pp) {
  const iso = pp.schedule_time ? pp.schedule_time.replace(" ", "T") : pp.at;
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return pp.schedule_time || "";
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }) + " " +
    d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

function PostpickrBadge({ pp, className = "" }) {
  const s = PP_STATUS[pp.status] || PP_STATUS.draft;
  const names = (pp.channels || []).map((c) => (c.name || "").trim()).filter(Boolean);
  const chLabel = names.length
    ? names.slice(0, 2).join(", ") + (names.length > 2 ? ` +${names.length - 2}` : "")
    : `${(pp.channels || []).length} canali`;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="postpickr-status-badge">
      <span className={`px-2 py-0.5 text-[0.58rem] uppercase tracking-widest ${s.cls}`}>{s.label}</span>
      <span className="text-[0.62rem] text-stone2e">{chLabel} · {ppDate(pp)}</span>
    </div>
  );
}

export default function PedBoard({ plan, onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [showSheet, setShowSheet] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetBusy, setSheetBusy] = useState(false);
  const dragId = useRef(null);

  const load = useCallback(() => {
    api.get(`/ped/plans/${plan.id}/items`).then(({ data }) => setItems(data)).finally(() => setLoading(false));
  }, [plan.id]);
  useEffect(() => { load(); }, [load]);

  const onDrop = async (targetId) => {
    const from = items.findIndex((i) => i.id === dragId.current);
    const to = items.findIndex((i) => i.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
    await api.post(`/ped/plans/${plan.id}/reorder`, { item_ids: next.map((i) => i.id) });
  };

  const importFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/ped/plans/${plan.id}/import`, fd);
      toast.success(`Importati ${data.imported} contenuti`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openHistory = async () => {
    const { data } = await api.get(`/ped/plans/${plan.id}/history`);
    setHistory(data);
    setShowHistory(true);
  };

  const importSheet = async () => {
    if (!sheetUrl.trim()) return;
    setSheetBusy(true);
    try {
      const { data } = await api.post(`/ped/plans/${plan.id}/import-sheet`, { url: sheetUrl.trim() });
      toast.success(`Importati ${data.imported} contenuti dal Google Sheet`);
      setShowSheet(false);
      setSheetUrl("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSheetBusy(false);
    }
  };

  const removeItem = async (item, e) => {
    e.stopPropagation();
    if (!window.confirm(`Eliminare "${item.title}"?`)) return;
    await api.delete(`/ped/items/${item.id}`);
    load();
  };

  return (
    <div>
      <button onClick={onBack} data-testid="back-to-plans" className="flex items-center gap-2 text-stone2e hover:text-ink transition-colors mb-2">
        <ArrowLeft strokeWidth={1.5} size={18} /><span className="font-display text-3xl text-ink">{plan.name}</span>
      </button>
      <p className="meta-label mb-8">PIANO EDITORIALE</p>

      <div className="flex flex-wrap items-center gap-3 mb-10">
        <button onClick={() => setEditing({})} data-testid="new-content-button" className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors">
          <Plus size={14} strokeWidth={1.5} /> Nuovo contenuto
        </button>
        <label className="inline-flex items-center gap-2 border border-black px-4 py-2.5 text-[0.65rem] uppercase tracking-widest cursor-pointer hover:bg-black hover:text-greige transition-colors" data-testid="import-button">
          <Upload size={13} strokeWidth={1.5} /> Importa Excel/CSV
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} />
        </label>
        <button onClick={() => setShowSheet(true)} data-testid="import-sheet-button" className="inline-flex items-center gap-2 border border-black px-4 py-2.5 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
          <FileSpreadsheet size={13} strokeWidth={1.5} /> Google Sheet
        </button>
        <a href={authUrl(`/ped/plans/${plan.id}/export?format=csv`)} data-testid="export-csv-button" className="inline-flex items-center gap-2 border border-black px-4 py-2.5 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
          <Download size={13} strokeWidth={1.5} /> CSV
        </a>
        <a href={authUrl(`/ped/plans/${plan.id}/export?format=xlsx`)} data-testid="export-xlsx-button" className="inline-flex items-center gap-2 border border-black px-4 py-2.5 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
          <Download size={13} strokeWidth={1.5} /> Excel
        </a>
        <button onClick={openHistory} data-testid="history-button" className="inline-flex items-center gap-2 border border-black px-4 py-2.5 text-[0.65rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
          <History size={13} strokeWidth={1.5} /> Cronologia
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-stone2e" size={24} /></div>
      ) : items.length === 0 ? (
        <p className="font-editorial text-xl text-stone2e italic py-10 text-center">Nessun contenuto. Creane uno o importa un piano.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div
              key={item.id}
              draggable
              onDragStart={() => (dragId.current = item.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(item.id)}
              onClick={() => setEditing(item)}
              data-testid={`ped-card-${item.id}`}
              className="group bg-white/70 border border-white hover:border-black/40 transition-colors p-5 cursor-grab active:cursor-grabbing"
            >
              <div className="flex items-start justify-between gap-2">
                <span className={`px-2 py-0.5 text-[0.6rem] uppercase tracking-widest ${item.status === "pubblicato" ? "bg-black text-greige" : "border border-black/30 text-stone2e"}`}>
                  {item.status === "pubblicato" ? "Pubblicato" : "Bozza"}
                </span>
                <button onClick={(e) => removeItem(item, e)} data-testid={`delete-content-${item.id}`} className="text-stone2e hover:text-ink opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
              {item.postpickr && <PostpickrBadge pp={item.postpickr} className="mt-2" />}
              <h4 className="font-display text-xl text-ink mt-3 leading-snug">{item.title}</h4>
              {item.media?.length > 0 && (
                <div className="mt-3 flex gap-1.5" data-testid={`card-media-${item.id}`}>
                  {item.media.slice(0, 3).map((m) => (
                    m.kind === "video"
                      ? <video key={m.id} src={mediaSrc(m.url)} className="h-14 w-14 object-cover border border-black/10" muted />
                      : <img key={m.id} src={mediaSrc(m.url)} alt="" className="h-14 w-14 object-cover border border-black/10" />
                  ))}
                  {item.media.length > 3 && <span className="h-14 w-14 flex items-center justify-center text-xs text-stone2e border border-black/10">+{item.media.length - 3}</span>}
                </div>
              )}
              {item.text && <p className="font-body text-xs text-stone2e mt-1.5 line-clamp-2">{item.text}</p>}
              <p className="meta-label mt-4">
                {(item.format || "—").toUpperCase()}{item.publish_date ? ` · ${formatDateShort(item.publish_date).toUpperCase()}` : ""}
              </p>
            </div>
          ))}
        </div>
      )}

      {editing !== null && <ContentDialog plan={plan} item={editing} onClose={() => { setEditing(null); load(); }} onSaved={() => { setEditing(null); load(); }} />}

      {showSheet && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowSheet(false)} data-testid="sheet-modal">
          <div className="bg-greige border border-black/20 max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-display text-2xl text-ink">Importa da Google Sheet</h4>
              <button onClick={() => setShowSheet(false)} data-testid="sheet-close" className="text-stone2e hover:text-ink"><X size={20} strokeWidth={1.5} /></button>
            </div>
            <p className="font-body text-xs text-stone2e mb-4">Condividi il foglio come «Chiunque abbia il link» (sola lettura). Colonne: titolo, formato, testo, data_pubblicazione, stato.</p>
            <input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" data-testid="sheet-url-input" className="field-line mb-6" />
            <button onClick={importSheet} disabled={sheetBusy || !sheetUrl.trim()} data-testid="sheet-import-confirm"
              className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
              {sheetBusy ? <Loader2 className="animate-spin" size={14} /> : <FileSpreadsheet size={14} strokeWidth={1.5} />} Importa
            </button>
          </div>
        </div>
      )}

      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowHistory(false)} data-testid="history-modal">
          <div className="bg-greige border border-black/20 max-w-md w-full p-7 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h4 className="font-display text-2xl text-ink">Cronologia</h4>
              <button onClick={() => setShowHistory(false)} data-testid="history-close" className="text-stone2e hover:text-ink"><X size={20} strokeWidth={1.5} /></button>
            </div>
            {history.length === 0 ? <p className="font-editorial italic text-stone2e">Nessuna modifica registrata.</p> : (
              <div className="space-y-3">
                {history.map((h) => (
                  <div key={h.id}>
                    <p className="font-body text-sm text-ink">{h.detail}</p>
                    <p className="meta-label mt-0.5">{h.user_name?.toUpperCase()} · {formatDateShort(h.created_at).toUpperCase()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ContentDialog({ plan, item, onClose, onSaved }) {
  const isNew = !item.id;
  const [f, setF] = useState({
    title: item.title || "", format: item.format || FORMATS[0],
    text: item.text || "", publish_date: item.publish_date || "",
  });
  const [busy, setBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiModel, setAiModel] = useState("openai");
  const [aiModels, setAiModels] = useState([{ id: "openai", label: "OpenAI gpt-5.4", available: true }]);
  const [ppOpen, setPpOpen] = useState(false);
  const [ppChannels, setPpChannels] = useState(null);
  const [ppSel, setPpSel] = useState([]);
  const [ppAction, setPpAction] = useState("draft");
  const [ppTarget, setPpTarget] = useState("feed");
  const [ppWhen, setPpWhen] = useState(item.publish_date ? `${item.publish_date}T09:00` : "");
  const [ppFirstComment, setPpFirstComment] = useState("");
  const [ppLink, setPpLink] = useState("");
  const [ppDraftDone, setPpDraftDone] = useState(false);
  const [ppBusy, setPpBusy] = useState(false);
  const [live, setLive] = useState(item.postpickr?.live || []);
  const [syncBusy, setSyncBusy] = useState(false);
  const [autoSync, setAutoSync] = useState(false);
  const POSTPICKR_APP = "https://next.postpickr.com/";

  const syncStatus = async (silent = false) => {
    setSyncBusy(true);
    try {
      const { data } = await api.post(`/ped/items/${item.id}/postpickr-sync`);
      setLive(data.live);
      if (!silent) {
        toast.success(data.matched ? `Stato aggiornato · ${data.matched} pubblicazioni trovate` : "Nessun post trovato su PostPickr (forse ancora in bozza)");
      }
    } catch (err) {
      if (!silent) toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSyncBusy(false);
    }
  };

  useEffect(() => {
    if (isNew || !item.postpickr) return;
    let alive = true;
    const run = async () => {
      if (!alive) return;
      try {
        const { data } = await api.post(`/ped/items/${item.id}/postpickr-sync`);
        if (alive) setLive(data.live);
      } catch { /* silent background sync */ }
    };
    setAutoSync(true);
    run().finally(() => { if (alive) setAutoSync(false); });
    const t = setInterval(run, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [item.id, isNew, item.postpickr]);
  const [media, setMedia] = useState(item.media || []);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [aiImgOpen, setAiImgOpen] = useState(false);
  const [aiImgPrompt, setAiImgPrompt] = useState("");
  const [aiImgBusy, setAiImgBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState("instagram");
  const [brandInfo, setBrandInfo] = useState(null);
  const mediaDrag = useRef(null);

  useEffect(() => {
    if (!isNew) api.get(`/brands/${plan.brand_id}`).then(({ data }) => setBrandInfo(data)).catch(() => {});
  }, [plan.brand_id, isNew]);

  const genImage = async () => {
    setAiImgBusy(true);
    try {
      const { data } = await api.post(`/ped/items/${item.id}/generate-image`, { prompt: aiImgPrompt });
      setMedia(data.media);
      toast.success("Immagine generata con AI");
      setAiImgOpen(false);
      setAiImgPrompt("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiImgBusy(false);
    }
  };

  const dropMedia = async (targetId) => {
    const srcId = mediaDrag.current;
    mediaDrag.current = null;
    if (!srcId || srcId === targetId) return;
    const ids = media.map((m) => m.id);
    const from = ids.indexOf(srcId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const next = [...media];
    next.splice(to, 0, next.splice(from, 1)[0]);
    setMedia(next);
    try {
      await api.post(`/ped/items/${item.id}/media/reorder`, { media_ids: next.map((m) => m.id) });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error("File troppo grande (max 200 MB)"); return; }
    setMediaBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/ped/items/${item.id}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia(data.media);
      toast.success("Media caricato");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setMediaBusy(false);
    }
  };

  const deleteMedia = async (mid) => {
    try {
      const { data } = await api.delete(`/ped/items/${item.id}/media/${mid}`);
      setMedia(data.media);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/ai/models").then(({ data }) => { if (data?.length) setAiModels(data); }).catch(() => {});
  }, []);

  const openPP = async () => {
    setPpOpen((v) => !v);
    if (ppChannels) return;
    try {
      const { data } = await api.get("/postpickr/post-channels");
      setPpChannels(data.channels || []);
    } catch (err) {
      setPpChannels([]);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleCh = (id) => setPpSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const publishPP = async () => {
    if (!ppSel.length) { toast.error("Seleziona almeno un canale"); return; }
    if (ppAction === "schedule" && !ppWhen) { toast.error("Indica data e ora"); return; }
    setPpBusy(true);
    try {
      await api.patch(`/ped/items/${item.id}`, { ...f, publish_date: f.publish_date || undefined });
      const body = { channel_ids: ppSel, action: ppAction, target: ppTarget, first_comment: ppFirstComment, link: ppLink };
      if (ppAction === "schedule") body.schedule_time = ppWhen;
      const { data } = await api.post(`/ped/items/${item.id}/postpickr-publish`, body);
      const label = ppAction === "draft" ? "Bozza creata su PostPickr"
        : ppAction === "now" ? "Pubblicato su PostPickr" : "Programmato su PostPickr";
      toast.success(`${label} · ${data.channels} canali`);
      if (data.warnings) toast.info("Verifica il media del post nella dashboard PostPickr");
      if (ppAction === "draft") setPpDraftDone(true);
      else setPpOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPpBusy(false);
    }
  };

  const generateAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/generate-copy", {
        brand_id: plan.brand_id, brief: aiBrief, format: f.format, title: f.title, model: aiModel,
      });
      setF((prev) => ({ ...prev, text: data.text }));
      toast.success(`Testo generato con ${data.model || "AI"}`);
      setAiOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const save = async (publish) => {
    if (!f.title.trim()) {
      toast.error("Il titolo è obbligatorio");
      return;
    }
    setBusy(true);
    try {
      let id = item.id;
      if (isNew) {
        const { data } = await api.post(`/ped/plans/${plan.id}/items`, { ...f, publish_date: f.publish_date || null });
        id = data.id;
      } else {
        await api.patch(`/ped/items/${id}`, { ...f, publish_date: f.publish_date || undefined });
      }
      if (publish) {
        await api.post(`/ped/items/${id}/publish`);
        toast.success("Pubblicato sul calendario · 2 task generate");
      } else {
        toast.success("Salvato come bozza");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="content-dialog">
      <div className="bg-greige border border-black/20 max-w-lg w-full p-7 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h4 className="font-display text-2xl text-ink">{isNew ? "Nuovo contenuto" : "Modifica contenuto"}</h4>
          <button onClick={onClose} data-testid="content-dialog-close" className="text-stone2e hover:text-ink"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <div className="space-y-5">
          {item.postpickr && (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <PostpickrBadge pp={item.postpickr} />
                <button onClick={() => syncStatus()} disabled={syncBusy} data-testid="postpickr-sync-button"
                  className="inline-flex items-center gap-1.5 meta-label link-underline disabled:opacity-60">
                  {syncBusy ? <Loader2 className="animate-spin" size={12} /> : <History size={12} strokeWidth={1.5} />} Aggiorna stato
                </button>
                {autoSync && <span className="meta-label text-stone2e" data-testid="postpickr-auto-sync">AGGIORNAMENTO…</span>}
              </div>
              {live.length > 0 && (
                <div className="space-y-1" data-testid="postpickr-live-status">
                  {live.map((l, i) => (
                    <div key={i} className="flex items-center gap-2 text-[0.68rem] text-stone2e">
                      <span className={`px-1.5 py-0.5 uppercase tracking-wider text-white ${String(l.status_key) === "3" || (l.status || "").toLowerCase().includes("pubblic") ? "bg-emerald-700" : (l.error ? "bg-red-600" : "bg-amber-600")}`}>{l.status || "—"}</span>
                      <span className="text-ink">{l.name}</span>
                      {l.date && <span>· {l.date}</span>}
                      {l.error && <span className="text-red-600">· {l.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titolo *" data-testid="content-title-input" className="field-line" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="meta-label mb-1.5">FORMATO</p>
              <select value={f.format} onChange={(e) => setF({ ...f, format: e.target.value })} data-testid="content-format-select" className="field-line bg-transparent">
                {FORMATS.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <p className="meta-label mb-1.5">DATA PUBBLICAZIONE</p>
              <input type="date" value={f.publish_date} onChange={(e) => setF({ ...f, publish_date: e.target.value })} data-testid="content-date-input" className="field-line" />
            </div>
          </div>
          <textarea rows={5} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} placeholder="Testo del contenuto…" data-testid="content-text-input" className="field-line resize-none" />
          {!aiOpen ? (
            <button onClick={() => setAiOpen(true)} data-testid="ai-generate-button"
              className="inline-flex items-center gap-2 border border-black/40 text-ink px-4 py-2 text-[0.62rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
              <Sparkles size={13} strokeWidth={1.5} /> Genera con AI
            </button>
          ) : (
            <div className="border border-black/20 bg-white/60 p-4 space-y-3" data-testid="ai-panel">
              <p className="meta-label">GENERA NEL TONO DI VOCE DEL BRAND</p>
              <div>
                <p className="meta-label mb-1.5">MODELLO AI</p>
                <select value={aiModel} onChange={(e) => setAiModel(e.target.value)} data-testid="ai-model-select" className="field-line bg-transparent">
                  {aiModels.map((m) => (
                    <option key={m.id} value={m.id} disabled={!m.available}>
                      {m.label}{m.available ? "" : " · non configurato"}
                    </option>
                  ))}
                </select>
              </div>
              <textarea rows={2} value={aiBrief} onChange={(e) => setAiBrief(e.target.value)} placeholder="Brief: di cosa parla il post, obiettivo, dettagli…" data-testid="ai-brief-input" className="field-line resize-none" />
              <div className="flex items-center gap-3">
                <button onClick={generateAI} disabled={aiBusy || !aiBrief.trim()} data-testid="ai-generate-confirm"
                  className="inline-flex items-center gap-2 bg-black text-greige px-4 py-2 text-[0.62rem] uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
                  {aiBusy ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} strokeWidth={1.5} />} {aiBusy ? "Genero…" : "Genera testo"}
                </button>
                <button onClick={() => setAiOpen(false)} data-testid="ai-cancel" className="meta-label link-underline">Annulla</button>
              </div>
            </div>
          )}

          {!isNew && (
            <div className="border-t border-black/10 pt-5">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="meta-label">MEDIA · IMMAGINI E VIDEO</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setAiImgOpen((v) => !v)} data-testid="ai-image-toggle"
                    className={`inline-flex items-center gap-1.5 border border-black px-3 py-2 text-[0.6rem] uppercase tracking-widest transition-colors ${aiImgOpen ? "bg-black text-greige" : "hover:bg-black hover:text-greige"}`}>
                    <Wand2 size={12} strokeWidth={1.5} /> Immagine AI
                  </button>
                  <label className={`inline-flex items-center gap-1.5 border border-black px-3 py-2 text-[0.6rem] uppercase tracking-widest cursor-pointer transition-colors ${mediaBusy ? "opacity-60" : "hover:bg-black hover:text-greige"}`} data-testid="media-upload-label">
                    {mediaBusy ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} strokeWidth={1.5} />}
                    {mediaBusy ? "Carico…" : "Carica"}
                    <input type="file" accept="image/*,video/*" className="hidden" data-testid="media-upload-input"
                      disabled={mediaBusy} onChange={(e) => { uploadMedia(e.target.files?.[0]); e.target.value = ""; }} />
                  </label>
                </div>
              </div>
              {aiImgOpen && (
                <div className="border border-black/20 bg-white/60 p-4 space-y-3 mb-3" data-testid="ai-image-panel">
                  <p className="meta-label">GENERA UN'IMMAGINE ON-BRAND</p>
                  <textarea rows={2} value={aiImgPrompt} onChange={(e) => setAiImgPrompt(e.target.value)} placeholder="Descrivi l'immagine (vuoto = usa titolo e testo del contenuto)…" data-testid="ai-image-prompt" className="field-line resize-none" />
                  <div className="flex items-center gap-3">
                    <button onClick={genImage} disabled={aiImgBusy} data-testid="ai-image-generate"
                      className="inline-flex items-center gap-2 bg-black text-greige px-4 py-2 text-[0.62rem] uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
                      {aiImgBusy ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} strokeWidth={1.5} />} {aiImgBusy ? "Genero l'immagine…" : "Genera e allega"}
                    </button>
                    <button onClick={() => setAiImgOpen(false)} className="meta-label link-underline">Annulla</button>
                  </div>
                </div>
              )}
              {media.length === 0 ? (
                <p className="font-editorial italic text-stone2e text-sm">Nessun media. Carica immagini/video o genera un'immagine AI da allegare alla pubblicazione.</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="media-grid">
                    {media.map((m) => (
                      <div key={m.id} draggable
                        onDragStart={() => (mediaDrag.current = m.id)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => dropMedia(m.id)}
                        className="relative group/media aspect-square border border-black/10 overflow-hidden bg-black/5 cursor-grab active:cursor-grabbing" data-testid={`media-item-${m.id}`}>
                        {m.kind === "video"
                          ? <video src={mediaSrc(m.url)} className="w-full h-full object-cover" controls />
                          : <img src={mediaSrc(m.url)} alt={m.filename} className="w-full h-full object-cover" />}
                        {m.ai && <span className="absolute bottom-1 left-1 bg-black/70 text-greige px-1 py-0.5 text-[0.5rem] uppercase tracking-wider inline-flex items-center gap-0.5"><Wand2 size={8} /> AI</span>}
                        <span className="absolute top-1 left-1 bg-black/50 text-greige p-0.5 opacity-0 group-hover/media:opacity-100 transition-opacity"><GripVertical size={11} /></span>
                        <button onClick={() => deleteMedia(m.id)} data-testid={`media-delete-${m.id}`}
                          className="absolute top-1 right-1 bg-black/70 text-greige p-1 opacity-0 group-hover/media:opacity-100 transition-opacity">
                          <Trash2 size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[0.62rem] text-stone2e">Trascina per ordinare il carosello.</p>
                    <button onClick={() => setPreviewOpen(true)} data-testid="ig-preview-button"
                      className="inline-flex items-center gap-1.5 meta-label link-underline">
                      <Eye size={12} strokeWidth={1.5} /> Anteprima Instagram
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {!isNew && (
            <div className="border-t border-black/10 pt-5">
              {!ppOpen ? (
                <button onClick={openPP} data-testid="postpickr-publish-toggle"
                  className="inline-flex items-center gap-2 border border-black/40 text-ink px-4 py-2 text-[0.62rem] uppercase tracking-widest hover:bg-black hover:text-greige transition-colors">
                  <Zap size={13} strokeWidth={1.5} /> Pubblica su PostPickr
                </button>
              ) : (
                <div className="border border-black/20 bg-white/60 p-4 space-y-4" data-testid="postpickr-publish-panel">
                  <p className="meta-label">PUBBLICA O PROGRAMMA SU POSTPICKR</p>
                  {!ppChannels ? (
                    <p className="font-body text-sm text-stone2e flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Carico i canali…</p>
                  ) : !ppChannels.length ? (
                    <p className="font-body text-sm text-stone2e">Nessun canale collegato in PostPickr.</p>
                  ) : (
                    <>
                      <div>
                        <p className="meta-label mb-2">CANALI</p>
                        <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                          {ppChannels.map((c) => (
                            <label key={c.id} className="flex items-center gap-2 text-sm text-ink cursor-pointer" data-testid={`pp-channel-${c.id}`}>
                              <input type="checkbox" checked={ppSel.includes(c.id)} onChange={() => toggleCh(c.id)} className="accent-black" />
                              <span className="truncate"><span className="meta-label mr-1">{c.platform}</span>{c.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="meta-label mb-1.5">AZIONE</p>
                          <select value={ppAction} onChange={(e) => setPpAction(e.target.value)} data-testid="pp-action-select" className="field-line bg-transparent">
                            <option value="draft">Salva come bozza</option>
                            <option value="schedule">Programma</option>
                            <option value="now">Pubblica ora</option>
                          </select>
                        </div>
                        <div>
                          <p className="meta-label mb-1.5">DESTINAZIONE</p>
                          <select value={ppTarget} onChange={(e) => setPpTarget(e.target.value)} data-testid="pp-target-select" className="field-line bg-transparent">
                            <option value="feed">Feed</option>
                            <option value="story">Storia</option>
                            <option value="reels">Reels</option>
                          </select>
                        </div>
                      </div>
                      {ppAction === "schedule" && (
                        <div>
                          <p className="meta-label mb-1.5">QUANDO</p>
                          <input type="datetime-local" value={ppWhen} onChange={(e) => setPpWhen(e.target.value)} data-testid="pp-when-input" className="field-line" />
                        </div>
                      )}
                      <div>
                        <p className="meta-label mb-1.5">PRIMO COMMENTO (OPZIONALE)</p>
                        <textarea rows={2} value={ppFirstComment} onChange={(e) => setPpFirstComment(e.target.value)} placeholder="Es. hashtag o menzioni da mettere nel primo commento…" data-testid="pp-first-comment" className="field-line resize-none" />
                      </div>
                      <div>
                        <p className="meta-label mb-1.5">LINK (OPZIONALE)</p>
                        <input type="url" value={ppLink} onChange={(e) => setPpLink(e.target.value)} placeholder="https://…" data-testid="pp-link-input" className="field-line" />
                      </div>
                      <p className="font-body text-[0.7rem] text-stone2e">Collaborazioni, tag di altri account e luogo non sono gestiti dall'API: usa «Salva come bozza» e completali dentro PostPickr prima di pubblicare.</p>
                      {ppDraftDone && (
                        <a href={POSTPICKR_APP} target="_blank" rel="noopener noreferrer" data-testid="open-postpickr-draft"
                          className="flex items-center justify-center gap-2 border border-black bg-emerald-700 text-white px-4 py-2.5 text-[0.62rem] uppercase tracking-widest hover:bg-emerald-800 transition-colors">
                          <Zap size={13} strokeWidth={1.5} /> Apri PostPickr → Contenuti → Bozze (tag, collaboratori, luogo)
                        </a>
                      )}
                      {ppAction !== "draft" && ppSel.some((id) => ["instagram", "tiktok"].includes(ppChannels.find((c) => c.id === id)?.platform)) && (
                        <p className="font-body text-[0.7rem] text-stone2e">Instagram e TikTok richiedono un media per la pubblicazione diretta. Senza immagine/video usa «Salva come bozza» e completa da PostPickr.</p>
                      )}
                      <div className="flex items-center gap-3">
                        <button onClick={publishPP} disabled={ppBusy} data-testid="pp-publish-confirm"
                          className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
                          {ppBusy ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} strokeWidth={1.5} />}
                          {ppBusy ? "Invio…" : ppAction === "draft" ? "Crea bozza" : ppAction === "now" ? "Pubblica ora" : "Programma"}
                        </button>
                        <button onClick={() => setPpOpen(false)} data-testid="pp-cancel" className="meta-label link-underline">Annulla</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-3 mt-8">
          <button onClick={() => save(false)} disabled={busy} data-testid="save-draft-button"
            className="border border-black px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-black hover:text-greige transition-colors disabled:opacity-60">
            Salva senza pubblicare
          </button>
          {item.status !== "pubblicato" && (
            <button onClick={() => save(true)} disabled={busy} data-testid="publish-button"
              className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
              <CalendarCheck size={14} strokeWidth={1.5} /> Pubblica
            </button>
          )}
        </div>
      </div>
      {previewOpen && (() => {
        const handle = (brandInfo?.name || "brand").toLowerCase().replace(/\s+/g, "_");
        const caption = f.text || item.text || "";
        const first = media[0];
        const firstEl = (cls) => first && (first.kind === "video"
          ? <video src={mediaSrc(first.url)} className={cls} controls />
          : <img src={mediaSrc(first.url)} className={cls} alt="" />);
        const avatar = <div className="h-8 w-8 rounded-full bg-gradient-to-tr from-amber-500 to-pink-600 flex items-center justify-center text-white text-xs font-bold shrink-0">{(brandInfo?.name || "B").charAt(0).toUpperCase()}</div>;
        const tabs = [["instagram", "Instagram"], ["facebook", "Facebook"], ["tiktok", "TikTok"]];
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={(e) => { e.stopPropagation(); setPreviewOpen(false); }} data-testid="ig-preview-modal">
            <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-1">
                  {tabs.map(([id, label]) => (
                    <button key={id} onClick={() => setPreviewPlatform(id)} data-testid={`preview-tab-${id}`}
                      className={`px-3 py-1.5 text-[0.6rem] uppercase tracking-widest transition-colors ${previewPlatform === id ? "bg-white text-ink" : "bg-white/30 text-white hover:bg-white/50"}`}>{label}</button>
                  ))}
                </div>
                <button onClick={() => setPreviewOpen(false)} data-testid="ig-preview-close" className="text-white/80 hover:text-white"><X size={20} /></button>
              </div>

              {previewPlatform === "instagram" && (
                <div className="bg-white overflow-hidden" data-testid="preview-instagram">
                  <div className="flex items-center gap-2 p-3 border-b border-black/10">
                    {avatar}<span className="font-body text-sm font-semibold text-ink">{handle}</span>
                  </div>
                  <div className="aspect-square bg-black">{firstEl("w-full h-full object-cover")}</div>
                  <div className="flex items-center gap-4 px-3 pt-3 text-ink"><Heart size={22} strokeWidth={1.5} /><MessageCircle size={22} strokeWidth={1.5} /><Send size={22} strokeWidth={1.5} /><Bookmark size={22} strokeWidth={1.5} className="ml-auto" /></div>
                  <div className="px-3 py-2 text-sm text-ink max-h-32 overflow-y-auto"><span className="font-semibold">{handle}</span> <span className="whitespace-pre-wrap">{caption}</span></div>
                </div>
              )}

              {previewPlatform === "facebook" && (
                <div className="bg-white overflow-hidden" data-testid="preview-facebook">
                  <div className="flex items-center gap-2 p-3">
                    {avatar}<div><p className="font-body text-sm font-semibold text-ink leading-tight">{brandInfo?.name || "Brand"}</p><p className="text-[0.62rem] text-stone2e">Adesso · 🌐</p></div>
                  </div>
                  <div className="px-3 pb-2 text-sm text-ink whitespace-pre-wrap max-h-24 overflow-y-auto">{caption}</div>
                  <div className="aspect-video bg-black">{firstEl("w-full h-full object-cover")}</div>
                  <div className="grid grid-cols-3 border-t border-black/10 text-stone2e text-xs py-2">
                    <span className="flex items-center justify-center gap-1.5"><ThumbsUp size={15} strokeWidth={1.5} /> Mi piace</span>
                    <span className="flex items-center justify-center gap-1.5"><MessageCircle size={15} strokeWidth={1.5} /> Commenta</span>
                    <span className="flex items-center justify-center gap-1.5"><Share2 size={15} strokeWidth={1.5} /> Condividi</span>
                  </div>
                </div>
              )}

              {previewPlatform === "tiktok" && (
                <div className="relative bg-black overflow-hidden mx-auto" style={{ aspectRatio: "9/16", maxHeight: "70vh" }} data-testid="preview-tiktok">
                  {firstEl("absolute inset-0 w-full h-full object-cover")}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
                  <div className="absolute right-2 bottom-24 flex flex-col items-center gap-5 text-white">
                    {avatar}
                    <span className="flex flex-col items-center text-[0.6rem]"><Heart size={26} strokeWidth={1.5} />0</span>
                    <span className="flex flex-col items-center text-[0.6rem]"><MessageCircle size={26} strokeWidth={1.5} />0</span>
                    <span className="flex flex-col items-center text-[0.6rem]"><Share2 size={26} strokeWidth={1.5} />0</span>
                  </div>
                  <div className="absolute left-3 right-14 bottom-4 text-white">
                    <p className="font-body text-sm font-semibold">@{handle}</p>
                    <p className="text-xs whitespace-pre-wrap line-clamp-3 mt-1">{caption}</p>
                    <p className="text-[0.62rem] mt-2 flex items-center gap-1.5"><Music2 size={12} /> suono originale · {brandInfo?.name || "brand"}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
